import { useEffect, useMemo, useState } from "react";
import { Library, Search } from "lucide-react";
import {
  getCollections,
  getDocumentPreview,
  getFilters,
  getPinnedSources,
  pinSource,
  recordInteractionLog,
  searchDocuments,
  unpinSource,
  updateInteractionLog,
} from "../lib/documentSearch/documentSearchClient";
import { emptyFilters, toggleValue } from "../lib/documentSearch/filters";
import type {
  Collection,
  DocumentPreview,
  FilterOptions,
  InteractionEventType,
  SearchFilters,
  SearchResponse,
  SourceDocument,
} from "../lib/documentSearch/types";
import { AgentMessage } from "./AgentMessage";
import { BrandRibbon } from "./BrandRibbon";
import { DocumentChatWorkspace } from "./DocumentChatWorkspace";
import { PinnedSourcesTray } from "./PinnedSourcesTray";
import { RefinementChips } from "./RefinementChips";
import { SearchComposer } from "./SearchComposer";
import { SourcePreviewModal } from "./SourcePreviewModal";
import { SourceResults } from "./SourceResults";
import { SourceSidebar } from "./SourceSidebar";
import { SourceSetBrowser } from "./SourceSetBrowser";
import { TopBar } from "./TopBar";
import { InteractionLogsPanel } from "./InteractionLogsPanel";

type MobileView = "search" | "sources";
type WorkspaceView = "search" | "logs" | "training";

export function DocumentSearchAgentPage() {
  const isEmbedded =
    typeof window !== "undefined" && window.self !== window.top;
  const [collections, setCollections] = useState<Collection[]>([]);
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
  const [filters, setFilters] = useState<SearchFilters>(emptyFilters);
  const [activeQuery, setActiveQuery] = useState("");
  const [threshold, setThreshold] = useState(0.5);
  const [maxSources, setMaxSources] = useState(25);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchResponse, setSearchResponse] = useState<SearchResponse | null>(null);
  const [selectedResultId, setSelectedResultId] = useState<string | null>(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [preview, setPreview] = useState<DocumentPreview | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [pinnedSources, setPinnedSources] = useState<SourceDocument[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [mobileView, setMobileView] = useState<MobileView>("search");
  const [workspaceView, setWorkspaceView] = useState<WorkspaceView>("search");
  const [activeSearchLogId, setActiveSearchLogId] = useState<string | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatSourceIds, setChatSourceIds] = useState<string[]>([]);

  useEffect(() => {
    let mounted = true;
    Promise.all([getCollections(), getFilters(), getPinnedSources()]).then(
      ([nextCollections, nextFilters, nextPinnedSources]) => {
        if (!mounted) {
          return;
        }
        setCollections(nextCollections);
        setFilterOptions(nextFilters);
        setPinnedSources(nextPinnedSources);
      },
    );
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!hasSearched) {
      setIsSearching(false);
      return;
    }

    let mounted = true;
    const startedAt = performance.now();
    setIsSearching(true);
    searchDocuments(activeQuery, filters, { threshold, maxSources })
      .then((response) => {
        if (!mounted) {
          return;
        }
        setSearchResponse(response);
        setActiveSearchLogId(response.logId ?? null);
        setChatSourceIds((current) =>
          current.filter((documentId) =>
            response.results.some(
              (document) =>
                sourceIdFor(document) === documentId && document.chatSummary?.available,
            ),
          ),
        );
        setSelectedResultId((current) => {
          if (current) {
            const currentResult = response.results.find((document) => document.id === current);
            if (currentResult) {
              setSelectedDocumentId(sourceIdFor(currentResult));
              return current;
            }
          }
          const firstResult = response.results[0];
          setSelectedDocumentId(firstResult ? sourceIdFor(firstResult) : null);
          return firstResult?.id ?? null;
        });
      })
      .catch((error) => {
        if (!mounted) {
          return;
        }
        void recordInteractionLog({
          eventType: "search_error",
          query: activeQuery,
          filters,
          threshold,
          maxSources,
          durationMs: Math.round(performance.now() - startedAt),
          payload: {
            message: error instanceof Error ? error.message : "Search failed",
          },
        });
        setSearchResponse({
          query: activeQuery,
          total: 0,
          results: [],
          refinements: [],
        });
        setActiveSearchLogId(null);
        setSelectedResultId(null);
        setSelectedDocumentId(null);
      })
      .finally(() => {
        if (mounted) {
          setIsSearching(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, [activeQuery, filters, hasSearched, threshold, maxSources]);

  useEffect(() => {
    let mounted = true;
    if (!selectedDocumentId) {
      setPreview(null);
      return () => {
        mounted = false;
      };
    }
    setPreview(null);
    getDocumentPreview(selectedDocumentId).then((nextPreview) => {
      if (mounted) {
        setPreview(nextPreview);
      }
    });
    return () => {
      mounted = false;
    };
  }, [selectedDocumentId]);

  const pinnedIds = useMemo(
    () => pinnedSources.map((source) => source.id),
    [pinnedSources],
  );
  const activeCollection = useMemo(
    () =>
      collections.find(
        (collection) => collection.id === filters.collections.at(-1),
      ) ?? null,
    [collections, filters.collections],
  );
  const selectedIsPinned = selectedDocumentId
    ? pinnedIds.includes(selectedDocumentId)
    : false;
  const selectedChatSources = useMemo(() => {
    const byDocumentId = new Map<string, SourceDocument>();
    for (const source of searchResponse?.results ?? []) {
      const documentId = sourceIdFor(source);
      if (!byDocumentId.has(documentId)) {
        byDocumentId.set(documentId, source);
      }
    }
    return chatSourceIds
      .map((documentId) => byDocumentId.get(documentId))
      .filter((source): source is SourceDocument => Boolean(source));
  }, [chatSourceIds, searchResponse]);

  async function handlePinToggle(document: SourceDocument | string) {
    const documentId = typeof document === "string" ? document : sourceIdFor(document);
    const wasPinned = pinnedIds.includes(documentId);
    const nextPinned = wasPinned
      ? await unpinSource(documentId)
      : await pinSource(documentId);
    setPinnedSources(nextPinned);
    logSourceInteraction(
      wasPinned ? "source_unpin" : "source_pin",
      document,
      wasPinned ? "pin_remove" : "pin_add",
      !wasPinned,
    );
  }

  function handleSearch(query: string) {
    setPreviewOpen(false);
    setActiveQuery(query.trim());
    setHasSearched(true);
    setMobileView("search");
    setWorkspaceView("search");
    setChatOpen(false);
  }

  function handleClearResponses() {
    setHasSearched(false);
    setActiveQuery("");
    setAdvancedOpen(false);
    setSearchResponse(null);
    setActiveSearchLogId(null);
    setSelectedResultId(null);
    setSelectedDocumentId(null);
    setPreview(null);
    setPreviewOpen(false);
    setIsSearching(false);
    setMobileView("search");
    setChatOpen(false);
    setChatSourceIds([]);
  }

  function handleChatSourceToggle(document: SourceDocument) {
    const documentId = sourceIdFor(document);
    const summaryId = document.chatSummary?.summaryId;
    setChatSourceIds((current) => {
      if (current.includes(documentId)) {
        return current.filter((id) => id !== documentId);
      }
      const withoutSameSummary = summaryId
        ? current.filter((id) => {
            const selected = searchResponse?.results.find(
              (result) => sourceIdFor(result) === id,
            );
            return selected?.chatSummary?.summaryId !== summaryId;
          })
        : current;
      return withoutSameSummary.length < 6
        ? [...withoutSameSummary, documentId]
        : withoutSameSummary;
    });
  }

  function logSourceInteraction(
    eventType: InteractionEventType,
    document: SourceDocument | string,
    trigger: string,
    pinned?: boolean,
  ) {
    const documentId = typeof document === "string" ? document : sourceIdFor(document);
    const source = typeof document === "string" ? sourceForDocumentId(document) : document;
    void recordInteractionLog({
      eventType,
      relatedSearchLogId: activeSearchLogId ?? undefined,
      query: searchResponse?.query ?? activeQuery,
      resultMode: searchResponse?.mode,
      searchQuery: searchResponse?.searchQuery,
      filters,
      threshold,
      maxSources,
      totalResults: searchResponse?.total,
      sourceDocumentId: documentId,
      sourceTitle: source?.title,
      sourceRank: source ? rankForSource(source) : undefined,
      sourceUrl: source?.sourceUrl,
      pinned,
      payload: {
        trigger,
        resultId: typeof document === "string" ? undefined : document.id,
        collectionId: source?.collectionId,
        type: source?.type,
        year: source?.year,
        relevance: source?.relevance,
        tags: source?.tags,
      },
    });
  }

  function sourceForDocumentId(documentId: string): SourceDocument | null {
    return (
      searchResponse?.results.find((document) => sourceIdFor(document) === documentId) ??
      pinnedSources.find((document) => sourceIdFor(document) === documentId) ??
      (preview && sourceIdFor(preview) === documentId ? preview : null)
    );
  }

  function rankForSource(source: SourceDocument): number | undefined {
    const index = searchResponse?.results.findIndex(
      (document) => document.id === source.id || sourceIdFor(document) === sourceIdFor(source),
    );
    return index !== undefined && index >= 0 ? index + 1 : undefined;
  }

  function handleSummaryFeedback(
    label: "good_training_example" | "needs_adapter_fix",
    comment?: string,
  ) {
    if (!activeSearchLogId) {
      return;
    }
    const password = readStoredLogPassword() ?? window.prompt("Log password")?.trim();
    if (!password) {
      return;
    }
    storeLogPassword(password);
    const cleanedComment = comment?.trim();
    void updateInteractionLog(password, activeSearchLogId, {
      evalLabel: label,
      notes:
        label === "good_training_example"
          ? "Thumbs up on assistant summary."
          : cleanedComment
            ? `Thumbs down on assistant summary. Comment: ${cleanedComment}`
            : "Thumbs down on assistant summary.",
    }).catch((error) => {
      console.warn("Unable to save assistant summary feedback.", error);
    });
  }

  return (
    <div
      className={[
        "app-shell",
        workspaceView === "search" && chatOpen ? "app-shell--chat" : "",
        isEmbedded ? "app-shell--embedded" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <TopBar
        activeView={workspaceView}
        onSearchOpen={() => {
          setWorkspaceView("search");
          setChatOpen(false);
        }}
        onLogsOpen={() => {
          setWorkspaceView("logs");
          setChatOpen(false);
        }}
        onTrainingOpen={() => {
          setWorkspaceView("training");
          setChatOpen(false);
        }}
      />

      {workspaceView === "training" ? (
        <main className="training-workspace">
          <iframe
            className="training-workspace__frame"
            src="/training/eval_dashboard.html"
            title="Training evaluation dashboard"
          />
        </main>
      ) : workspaceView === "logs" ? (
        <main className="workspace workspace--logs">
          <InteractionLogsPanel />
        </main>
      ) : chatOpen ? (
        <DocumentChatWorkspace
          sources={selectedChatSources}
          initialQuery={activeQuery}
          relatedSearchLogId={activeSearchLogId}
          onBack={() => setChatOpen(false)}
        />
      ) : (
        <>
          <div className="mobile-tabs" role="tablist" aria-label="Workspace sections">
            {(["search", "sources"] as MobileView[]).map((view) => (
              <button
                key={view}
                type="button"
                className={mobileView === view ? "is-active" : ""}
                role="tab"
                aria-selected={mobileView === view}
                onClick={() => setMobileView(view)}
              >
                {view === "search" ? (
                  <Search size={17} aria-hidden="true" />
                ) : (
                  <Library size={17} aria-hidden="true" />
                )}
                <span>
                  {view[0].toUpperCase()}
                  {view.slice(1)}
                </span>
              </button>
            ))}
          </div>

          <main className="workspace workspace--preview-hidden">
        <div className={mobileView === "sources" ? "mobile-pane is-active" : "mobile-pane"}>
          <SourceSidebar
            collections={collections}
            filters={filters}
            filterOptions={filterOptions}
            onFiltersChange={setFilters}
            onResetFilters={() => setFilters(emptyFilters)}
            onCollectionBrowse={() => setMobileView("search")}
          />
        </div>

        <section
          className={[
            mobileView === "search" ? "search-pane is-active" : "search-pane",
            searchResponse ? "search-pane--results" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <div className="hero-panel">
            <BrandRibbon />
            <p className="section-kicker">myDigiGovSA Source Assistant</p>
            <h2>
              Find the right <span>PFMA source</span>
            </h2>
            <p>
              Search National Treasury material for departments, constitutional
              institutions, public entities, accounting officers, and accounting authorities.
            </p>
            <SearchComposer
              initialQuery={activeQuery}
              isSearching={isSearching}
              canClear={hasSearched || searchResponse !== null || selectedDocumentId !== null}
              advancedOpen={advancedOpen}
              threshold={threshold}
              maxSources={maxSources}
              onAdvancedToggle={() => setAdvancedOpen((current) => !current)}
              onThresholdChange={setThreshold}
              onMaxSourcesChange={setMaxSources}
              onSearch={handleSearch}
              onClear={handleClearResponses}
            />
          </div>

          <AgentMessage
            response={searchResponse}
            isSearching={isSearching}
            onFeedback={activeSearchLogId ? handleSummaryFeedback : undefined}
          />
          <RefinementChips
            refinements={searchResponse?.refinements ?? []}
            onSelect={(tag) =>
              setFilters((current) => ({
                ...current,
                tags: toggleValue(current.tags, tag),
              }))
            }
          />

          {searchResponse ? (
            <SourceResults
              results={searchResponse.results}
              selectedId={selectedResultId}
              pinnedIds={pinnedIds}
              chatSelectedIds={chatSourceIds}
              chatSelectionFull={chatSourceIds.length >= 6}
              onSelect={(document) => {
                const documentId = sourceIdFor(document);
                setSelectedResultId(document.id);
                if (selectedDocumentId !== documentId) {
                  setPreview(null);
                }
                setSelectedDocumentId(documentId);
                setPreviewOpen(true);
              }}
              onPinToggle={handlePinToggle}
              onChatToggle={handleChatSourceToggle}
              onChatStart={() => {
                if (selectedChatSources.length > 0) {
                  setPreviewOpen(false);
                  setChatOpen(true);
                  setMobileView("search");
                }
              }}
            />
          ) : null}

          {activeCollection && !searchResponse ? (
            <SourceSetBrowser
              collection={activeCollection}
              onSourceSelect={(document) => {
                const documentId = sourceIdFor(document);
                setSelectedResultId(null);
                if (selectedDocumentId !== documentId) {
                  setPreview(null);
                }
                setSelectedDocumentId(documentId);
                setPreviewOpen(true);
              }}
            />
          ) : null}

          <PinnedSourcesTray
            pinnedSources={pinnedSources}
            onSelect={(documentId) => {
              if (selectedDocumentId !== documentId) {
                setPreview(null);
              }
              setSelectedDocumentId(documentId);
              setPreviewOpen(true);
            }}
            onUnpin={(documentId) => void handlePinToggle(documentId)}
          />
        </section>

          </main>
        </>
      )}

      {previewOpen && preview ? (
        <SourcePreviewModal
          preview={preview}
          pinned={selectedIsPinned}
          onClose={() => setPreviewOpen(false)}
          onPinToggle={() => {
            if (selectedDocumentId) {
              void handlePinToggle(selectedDocumentId);
            }
          }}
          onOpenSource={() => logSourceInteraction("source_open", preview, "external_link")}
        />
      ) : null}
    </div>
  );
}

function sourceIdFor(document: SourceDocument): string {
  return document.sourceDocumentId ?? document.id;
}

function readStoredLogPassword(): string | null {
  try {
    const password = window.sessionStorage.getItem("mydigigovsa-pfma-log-password");
    return password?.trim() || null;
  } catch {
    return null;
  }
}

function storeLogPassword(password: string) {
  try {
    window.sessionStorage.setItem("mydigigovsa-pfma-log-password", password);
  } catch {
    // Ignore private browsing storage failures.
  }
}
